import { Box, Button, Grid, createStyles, makeStyles, Paper, Theme, Tooltip, Typography } from '@material-ui/core';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import ErrorIcon from '@material-ui/icons/Error';
import React from 'react';
import { AccountBalances } from '../gql-types.generated';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

import { isToday, isYesterday } from '../util/Dates';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      textAlign: 'start',
      [theme.breakpoints.down('sm')]: {
        margin: theme.spacing(2, 0),
      },
    },
    contentBox: {
      padding: theme.spacing(3),
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    headerText: {
      height: '100%',
      maxWidth: '85%',
    },
    headerHeadText: {
      letterSpacing: 1.5,
      fontSize: 12,
    },
    headerIcon: {
      lineHeight: 1,
      borderRadius: 50,
      backgroundColor: theme.palette.grey[200],
      padding: 8,
    },
    icon: {
      fontSize: '2rem',
      color: theme.palette.text.secondary,
    },
    headerWarningIcon: {
      lineHeight: 1,
      paddingRight: 8,
    },
    warningIcon: {
      fontSize: '1.5rem',
      color: theme.palette.error.main,
    },
    headerWarning: {
      display: 'flex',
      alignItems: 'center',
      '&:hover': {
        cursor: 'pointer',
        textDecoration: 'underline',
      },
    },
    warningBanner: {
      padding: theme.spacing(1.5, 3),
      display: 'flex',
      alignItems: 'center',
      backgroundColor: '#ffe7e6',
      color: theme.palette.error.main,
    },
    footerBox: {
      padding: theme.spacing(1, 3),
      display: 'flex',
      justifyContent: 'flex-end',
      borderTop: '1px solid #E0E0E0',
    },
    footerButton: {
      marginLeft: theme.spacing(2),
    },
    balanceDisplay: {
      fontSize: '3.25rem',
      fontWeight: 'lighter',
      margin: theme.spacing(2, 0),
    },
    balanceUpdated: {
      fontSize: '0.8rem',
      fontWeight: 'lighter',
      margin: theme.spacing(0),
    },
    tooltip: {
      cursor: 'pointer',
      marginLeft: '0.3rem',
    },
  }),
);

interface AccountBalanceProps {
  defaultCurrency?: string | null;
  displayPayoutBanner: boolean | undefined;
  settingsClickHandler: () => void;
  balances: AccountBalances | null | undefined;
}

const TotalAccountBalance: React.FC<AccountBalanceProps> = (props: AccountBalanceProps): JSX.Element | null => {
  const classes = useStyles();
  const { balances, defaultCurrency } = props;

  const payoutWarningBanner = (): JSX.Element | undefined => {
    const { displayPayoutBanner, settingsClickHandler } = props;
    if (displayPayoutBanner) {
      const ret = (
        <Box className={classes.warningBanner}>
          <Box className={classes.headerWarningIcon}>
            <ErrorIcon className={classes.warningIcon} />
          </Box>
          <Typography className={classes.headerWarning} onClick={settingsClickHandler}>
            Please update payout settings
          </Typography>
        </Box>
      );
      return ret;
    }
    return undefined;
  };

  const payoutBanner = payoutWarningBanner();
  // TODO: Default to browser locale if no defualt?
  const balanceValue = balances ? balances.balance : 0;
  const currentBalance = new Intl.NumberFormat('en', {
    style: 'currency',
    currencyDisplay: 'symbol',
    currency: defaultCurrency || 'USD',
  }).format((balanceValue || 0) / 100);

  let updatedTime = '(not synced)';
  if (balances && balances.updatedTimestamp) {
    const updatedDate = new Date(balances?.updatedTimestamp);
    if (isToday(updatedDate)) {
      updatedTime = `As of today at ${new Intl.DateTimeFormat('en', {
        hour: 'numeric',
        minute: 'numeric',
      }).format(updatedDate)}`;
    } else if (isYesterday(updatedDate)) {
      updatedTime = `As of yesterday at ${new Intl.DateTimeFormat('en', {
        hour: 'numeric',
        minute: 'numeric',
      }).format(updatedDate)}`;
    } else {
      updatedTime = `As of ${new Intl.DateTimeFormat('en', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      }).format(updatedDate)}`;
    }
  }

  return (
    <Paper className={classes.paper}>
      {payoutBanner}
      <Box className={classes.contentBox}>
        <Box className={classes.header}>
          <Box className={classes.headerText}>
            <Typography variant="subtitle2" className={classes.headerHeadText}>
              PAYOUT MANAGEMENT
            </Typography>
            <Typography variant="h6">Total Account Balance</Typography>
          </Box>
          <Box className={classes.headerIcon}>
            <CreditCardIcon className={classes.icon} />
          </Box>
        </Box>
        <Box>
          <Typography className={classes.balanceDisplay} variant="body1">
            {currentBalance}
          </Typography>
        </Box>
        <Grid container alignItems="center" alignContent="center">
          <Typography className={classes.balanceUpdated} variant="body1">
            {updatedTime}
          </Typography>
          <Tooltip title="Balance refreshes hourly" className={classes.tooltip}>
            <InfoOutlinedIcon htmlColor="grey" fontSize="small" />
          </Tooltip>
        </Grid>
      </Box>
      <Box className={classes.footerBox}>
        <Button className={classes.footerButton} color="primary" onClick={props.settingsClickHandler}>
          Settings
        </Button>
      </Box>
    </Paper>
  );
};

export default TotalAccountBalance;
